import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { umpiresAPI } from '../services/api';
import { useForm } from 'react-hook-form';

const UMPIRE_TYPES = ['on_field', 'third_umpire', 'match_referee', 'reserve'];
const UMPIRE_LEVELS = ['international', 'national', 'regional', 'local'];

function UmpireForm({ umpire, onClose }) {
  const queryClient = useQueryClient();
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: umpire || {
      name: '',
      email: '',
      phone: '',
      country: '',
      umpire_type: 'on_field',
      level: 'local',
      icc_panel: false,
      experience_years: 0,
    }
  });

  const createMutation = useMutation({
    mutationFn: umpiresAPI.createUmpire,
    onSuccess: () => {
      queryClient.invalidateQueries(['umpires']);
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => umpiresAPI.updateUmpire(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['umpires']);
      onClose();
    },
  });

  const onSubmit = (data) => {
    if (umpire?.id) {
      updateMutation.mutate({ id: umpire.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">{umpire ? 'Edit Umpire' : 'Add Umpire'}</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name *</label>
            <input
              {...register('name', { required: 'Name is required' })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                {...register('email')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                {...register('phone')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Country *</label>
            <input
              {...register('country', { required: 'Country is required' })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            {errors.country && <p className="text-red-500 text-sm">{errors.country.message}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Type</label>
              <select
                {...register('umpire_type')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {UMPIRE_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Level</label>
              <select
                {...register('level')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {UMPIRE_LEVELS.map(level => (
                  <option key={level} value={level}>
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Experience (Years)</label>
              <input
                type="number"
                {...register('experience_years', { valueAsNumber: true })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Certification</label>
              <input
                {...register('certification')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <label className="flex items-center">
            <input type="checkbox" {...register('icc_panel')} className="rounded border-gray-300" />
            <span className="ml-2 text-sm text-gray-700">ICC Elite/International Panel</span>
          </label>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Umpires() {
  const [showForm, setShowForm] = useState(false);
  const [editingUmpire, setEditingUmpire] = useState(null);
  const [filters, setFilters] = useState({ country: '', level: '', umpire_type: '' });
  const queryClient = useQueryClient();

  const { data: umpires = [], isLoading } = useQuery({
    queryKey: ['umpires', filters],
    queryFn: () => umpiresAPI.getUmpires(filters),
  });

  const deleteMutation = useMutation({
    mutationFn: umpiresAPI.deleteUmpire,
    onSuccess: () => queryClient.invalidateQueries(['umpires']),
  });

  const handleEdit = (umpire) => {
    setEditingUmpire(umpire);
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingUmpire(null);
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 'international': return 'bg-purple-100 text-purple-800';
      case 'national': return 'bg-blue-100 text-blue-800';
      case 'regional': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold">Umpires & Officials</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 w-full sm:w-auto text-center"
        >
          Add Umpire
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="Filter by country..."
          value={filters.country}
          onChange={(e) => setFilters({ ...filters, country: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md w-full"
        />
        <select
          value={filters.level}
          onChange={(e) => setFilters({ ...filters, level: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md w-full"
        >
          <option value="">All Levels</option>
          {UMPIRE_LEVELS.map(level => (
            <option key={level} value={level}>{level.charAt(0).toUpperCase() + level.slice(1)}</option>
          ))}
        </select>
        <select
          value={filters.umpire_type}
          onChange={(e) => setFilters({ ...filters, umpire_type: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md w-full"
        >
          <option value="">All Types</option>
          {UMPIRE_TYPES.map(type => (
            <option key={type} value={type}>
              {type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <p>Loading umpires...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-md">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Country</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Type</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Level</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Experience</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">Matches</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {umpires.map((umpire) => (
                <tr key={umpire.id}>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="font-medium text-gray-900">{umpire.name}</div>
                        {umpire.icc_panel && (
                          <span className="text-xs text-yellow-600">ICC Panel</span>
                        )}
                        <div className="text-xs text-gray-500 md:hidden">{umpire.country}</div>
                        <div className="text-xs text-gray-500 sm:hidden">
                          {umpire.umpire_type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">{umpire.country}</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                    {umpire.umpire_type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                    <span className={`px-2 py-1 text-xs rounded ${getLevelBadgeColor(umpire.level)}`}>
                      {umpire.level?.charAt(0).toUpperCase() + umpire.level?.slice(1)}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">{umpire.experience_years} years</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden xl:table-cell">
                    {umpire.matches_officiated}
                    {umpire.international_matches > 0 && (
                      <span className="text-xs text-gray-400"> ({umpire.international_matches} int.)</span>
                    )}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded ${umpire.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {umpire.is_available ? 'Available' : 'Unavailable'}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex flex-col sm:flex-row gap-1 sm:gap-3">
                      <button
                        onClick={() => handleEdit(umpire)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this umpire?')) {
                            deleteMutation.mutate(umpire.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <UmpireForm umpire={editingUmpire} onClose={handleClose} />}
    </div>
  );
}
